<template>
  <div class="api-docs-portal">
    <!-- Top Navigation Bar -->
    <header class="api-header">
      <div class="header-left">
        <router-link to="/" class="back-home-btn">
          <i class="fas fa-arrow-left"></i>
        </router-link>
        <div class="brand">
          <img src="@/assets/images/logo_vn_guide.png" class="brand-logo" alt="Logo" @error="handleLogoError" />
          <div class="brand-text">
            <h1 class="brand-title">KGVH Hưng Yên</h1>
            <p class="brand-subtitle">Tài liệu dự án & Thử nghiệm API</p>
          </div>
        </div>
      </div>

      <!-- Tab Controllers -->
      <div class="tab-controllers">
        <button
          :class="['tab-btn', { active: activeTab === 'docs' }]"
          @click="activeTab = 'docs'"
        >
          <i class="fas fa-book-open"></i> Tài liệu & Hướng dẫn
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'sandbox' }]"
          @click="activeTab = 'sandbox'"
        >
          <i class="fas fa-terminal"></i> Live Sandbox
        </button>
      </div>
    </header>

    <div class="portal-body">
      <!-- Sidebar Navigation -->
      <aside class="docs-sidebar">
        <!-- Document Type Selector -->
        <div class="doc-selector">
          <button
            :class="['doc-selector-btn', { active: activeDoc === 'overall' }]"
            @click="activeDoc = 'overall'; selectedSection = null"
          >
            Tổng quan dự án
          </button>
          <button
            :class="['doc-selector-btn', { active: activeDoc === 'details' }]"
            @click="activeDoc = 'details'; selectedSection = null"
          >
            Tài liệu API
          </button>
        </div>

        <div class="search-box">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Tìm kiếm tài liệu & endpoints..."
            class="search-input"
          />
        </div>

        <nav class="sidebar-nav">
          <div class="nav-group-title">Mục lục tài liệu</div>
          <div
            v-for="sec in filteredSections"
            :key="sec.id"
            :class="['nav-item', { active: selectedSection === sec.id }]"
            @click="scrollToSection(sec.id)"
          >
            <i :class="sec.icon"></i>
            <span>{{ sec.title }}</span>
          </div>
          <div v-if="filteredSections.length === 0 && filteredEndpoints.length === 0" class="empty-nav">
            Không tìm thấy kết quả
          </div>

          <!-- Matching Endpoints in Search Mode -->
          <template v-if="searchQuery && filteredEndpoints.length > 0">
            <div class="nav-group-title mt-4">Endpoints phù hợp ({{ filteredEndpoints.length }})</div>
            <div
              v-for="ep in filteredEndpoints"
              :key="ep.apiType + '-' + ep.method + '-' + ep.path"
              class="nav-item endpoint-nav-item"
              @click="scrollToSection(ep.sectionId)"
            >
              <span :class="['method-badge', ep.method.toLowerCase()]">{{ ep.method }}</span>
              <span class="endpoint-nav-path">{{ ep.path }}</span>
            </div>
          </template>
        </nav>
      </aside>

      <!-- Main Content Area -->
      <main ref="contentArea" class="docs-content">
        <!-- ==================== DOCUMENT 1: OVERALL REFERENCE ==================== -->
        <div v-if="activeDoc === 'overall'" class="document-view">
          <div class="doc-header">
            <h1 class="main-doc-title">KGVH Hưng Yên — Tổng Quan Dự Án (Overall Reference)</h1>
            <div class="doc-meta">
              <span class="meta-tag"><i class="far fa-calendar-alt"></i> Cập nhật: 2026-06-09</span>
            </div>
          </div>

          <hr class="doc-divider" />

          <!-- ov-purpose -->
          <section id="ov-purpose" class="doc-section">
            <h3 class="section-title"><i class="fas fa-info-circle"></i> Mục đích tài liệu này</h3>
            <div class="card card-accent-blue">
              <p>
                File này là tài liệu tham chiếu nhanh về toàn bộ dự án KGVH Hưng Yên — dành cho AI hoặc developer mới tiếp cận.
                <strong>Đọc file này trước khi bắt đầu bất kỳ task nào.</strong>
              </p>
            </div>
          </section>

          <!-- ov-whatis -->
          <section id="ov-whatis" class="doc-section">
            <h3 class="section-title"><i class="fas fa-question-circle"></i> Hệ thống là gì?</h3>
            <p>
              Backend cho ứng dụng hướng dẫn du lịch tỉnh Hưng Yên. Cung cấp nội dung địa điểm, hiện vật bảo tàng, bài viết và quản lý tổ chức.
            </p>
          </section>

          <!-- ov-apps -->
          <section id="ov-apps" class="doc-section">
            <h3 class="section-title"><i class="fas fa-cubes"></i> Hai ứng dụng độc lập</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>App</th>
                    <th>Entry point</th>
                    <th>Port</th>
                    <th>Docker Port</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Admin API</strong></td>
                    <td><code>admin_api.py</code></td>
                    <td>5000</td>
                    <td>8082</td>
                  </tr>
                  <tr>
                    <td><strong>Public API</strong></td>
                    <td><code>public_api.py</code></td>
                    <td>5001</td>
                    <td>8081</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="mt-2 text-muted-sub">Cả hai dùng chung database PostgreSQL, Redis, Elasticsearch.</p>
          </section>

          <!-- ov-urls -->
          <section id="ov-urls" class="doc-section">
            <h3 class="section-title"><i class="fas fa-link"></i> URL Production</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>API</th>
                    <th>URL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Admin API</strong></td>
                    <td><code class="text-secondary">https://admin.hy.khonggianvanhoa.vn/api/admin</code></td>
                  </tr>
                  <tr>
                    <td><strong>Public API</strong></td>
                    <td><code class="text-secondary">https://admin.hy.khonggianvanhoa.vn/api/public</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="mt-2 text-muted-sub">Staging: cung cấp theo yêu cầu riêng.</p>
          </section>

          <!-- ov-folders -->
          <section id="ov-folders" class="doc-section">
            <h3 class="section-title"><i class="fas fa-folder-open"></i> Cấu trúc thư mục quan trọng</h3>
            <div class="code-block-wrapper">
              <div class="code-block-header">
                <span class="code-lang">vn_guide/ Folder Structure</span>
              </div>
              <pre class="code-pre"><code>vn_guide/
├── apis/
│   ├── admin/resources/     # ~60 resource modules Admin
│   ├── public/resources/    # ~70 resource modules Public
│   ├── bases/               # Base classes: Resource, errors, auth handlers, utils
│   └── adapters/
├── models/postgres/         # 40+ SQLAlchemy ORM models
├── logics/                  # Business logic layer (KHÔNG gọi ORM trực tiếp từ API)
├── clients/                 # Google Storage, S3, Elasticsearch, Redis, OnePay
├── bases/                   # Base classes dùng chung (cache, email, MQ)
├── common/
│   ├── constants.py         # Enums, STRING_LENGTH, PAGINATION, PLACE_TYPES, v.v.
│   └── schemas.py           # Marshmallow base schemas
└── cron_tasks/              # Background jobs</code></pre>
            </div>
          </section>

          <!-- ov-patterns -->
          <section id="ov-patterns" class="doc-section">
            <h3 class="section-title"><i class="fas fa-cogs"></i> Pattern chính</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Pattern</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Resource → Logic → Model</strong></td>
                    <td>API không bao giờ gọi ORM trực tiếp</td>
                  </tr>
                  <tr>
                    <td><strong>Marshmallow schemas</strong></td>
                    <td>Validate input và serialize output</td>
                  </tr>
                  <tr>
                    <td><strong>Factory pattern</strong></td>
                    <td><code>api_factories.py</code> đăng ký resource động</td>
                  </tr>
                  <tr>
                    <td><strong>Translation models</strong></td>
                    <td>Mỗi content model có bảng <code>*Translation</code> kèm</td>
                  </tr>
                  <tr>
                    <td><strong>Multi-storage</strong></td>
                    <td>GCS và S3 VCCloud đều được hỗ trợ qua abstraction</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ov-auth -->
          <section id="ov-auth" class="doc-section">
            <h3 class="section-title"><i class="fas fa-shield-alt"></i> Xác thực</h3>
            <ul class="styled-list">
              <li><strong>JWT HS256</strong>, TTL 2 giờ</li>
              <li>Header: <code>Authorization: Bearer &lt;token&gt;</code></li>
              <li>Header tổ chức: <code>X-Organization-Code: &lt;code&gt;</code> (bắt buộc với role <code>moderator</code>)</li>
              <li>Header thiết bị: <code>Device-Id: &lt;uid&gt;</code> (bắt buộc trong Public API)</li>
            </ul>
          </section>

          <!-- ov-roles -->
          <section id="ov-roles" class="doc-section">
            <h3 class="section-title"><i class="fas fa-user-tag"></i> Roles</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Phạm vi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>administrator</code></td>
                    <td>Toàn quyền Admin API</td>
                  </tr>
                  <tr>
                    <td><code>editor</code></td>
                    <td>Admin API — nội dung</td>
                  </tr>
                  <tr>
                    <td><code>moderator</code></td>
                    <td>Admin API — giới hạn theo tổ chức</td>
                  </tr>
                  <tr>
                    <td><code>user</code></td>
                    <td>Public API</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ov-errors -->
          <section id="ov-errors" class="doc-section">
            <h3 class="section-title"><i class="fas fa-exclamation-triangle"></i> Mã lỗi HTTP</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Class</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>400</td>
                    <td><code>BadRequestParams</code></td>
                  </tr>
                  <tr>
                    <td>401</td>
                    <td><code>Unauthorized</code></td>
                  </tr>
                  <tr>
                    <td>403</td>
                    <td><code>PermissionError</code></td>
                  </tr>
                  <tr>
                    <td>405</td>
                    <td><code>MethodNotAllowed</code></td>
                  </tr>
                  <tr>
                    <td>422</td>
                    <td><code>UnprocessedEntity</code></td>
                  </tr>
                  <tr>
                    <td>500</td>
                    <td><code>ServerError</code></td>
                  </tr>
                  <tr>
                    <td>503</td>
                    <td><code>ServiceNotAvailable</code></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="code-block-wrapper mt-3">
              <div class="code-block-header">
                <span class="code-lang">Error Response Format</span>
              </div>
              <pre class="code-pre"><code>{"error": "ErrorCode", "message": "..."}</code></pre>
            </div>
          </section>

          <!-- ov-domains -->
          <section id="ov-domains" class="doc-section">
            <h3 class="section-title"><i class="fas fa-database"></i> Các domain dữ liệu chính</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>Models chính</th>
                    <th>Admin endpoints</th>
                    <th>Public endpoints</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Địa điểm</strong></td>
                    <td><code>Place</code>, <code>PlaceCategory</code>, <code>PlaceGroup</code></td>
                    <td><code>/place</code>, <code>/place/list</code></td>
                    <td><code>/place</code>, <code>/place/list</code></td>
                  </tr>
                  <tr>
                    <td><strong>Hiện vật</strong></td>
                    <td><code>Artifact</code>, <code>ArtifactClassification</code></td>
                    <td><code>/artifact</code></td>
                    <td><code>/artifact</code></td>
                  </tr>
                  <tr>
                    <td><strong>Media</strong></td>
                    <td><code>File</code>, <code>Storage</code></td>
                    <td><code>/storage</code></td>
                    <td><code>/storage</code></td>
                  </tr>
                  <tr>
                    <td><strong>Người dùng</strong></td>
                    <td><code>User</code>, <code>Device</code></td>
                    <td><code>/user</code>, <code>/user/list</code></td>
                    <td><code>/profile</code></td>
                  </tr>
                  <tr>
                    <td><strong>Tổ chức</strong></td>
                    <td><code>Organization</code>, <code>Department</code></td>
                    <td><code>/organization</code></td>
                    <td>—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ov-languages -->
          <section id="ov-languages" class="doc-section">
            <h3 class="section-title"><i class="fas fa-language"></i> Ngôn ngữ hỗ trợ</h3>
            <div class="lang-box text-center py-3">
              <span class="lang-tag">vi (Tiếng Việt)</span>
              <span class="lang-tag">en (English)</span>
              <span class="lang-tag">zh (中文)</span>
              <span class="lang-tag">ko (한국어)</span>
            </div>
            <p class="mt-2 text-muted-sub">Truyền qua param <code>locale</code> trong mọi request lấy nội dung.</p>
          </section>

          <!-- ov-pagination -->
          <section id="ov-pagination" class="doc-section">
            <h3 class="section-title"><i class="fas fa-list-ol"></i> Phân trang</h3>
            <ul class="styled-list">
              <li><strong>Param:</strong> <code>page</code> (mặc định 1), <code>per_page</code> (mặc định 50)</li>
              <li><strong>Response structure:</strong></li>
            </ul>
            <div class="code-block-wrapper">
              <pre class="code-pre"><code>{
  "items": [...],
  "total": N,
  "page": N,
  "per_page": N
}</code></pre>
            </div>
          </section>

          <!-- ov-background -->
          <section id="ov-background" class="doc-section">
            <h3 class="section-title"><i class="fas fa-sync"></i> Background & Sync</h3>
            <ul class="styled-list">
              <li><strong>RQ Worker</strong> (Redis Queue): xử lý file upload, Elasticsearch indexing async</li>
              <li><strong>RabbitMQ</strong>: event-driven Elasticsearch sync</li>
              <li><strong>Cron</strong>: <code>CodeCounting</code>, <code>CampaignStatusChecker</code></li>
              <li>Sau khi ghi dữ liệu qua Admin API → có thể có độ trễ vài giây trên search</li>
            </ul>
          </section>

          <!-- ov-docs -->
          <section id="ov-docs" class="doc-section">
            <h3 class="section-title"><i class="fas fa-file-alt"></i> Tài liệu liên quan</h3>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Nội dung</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>docs/api_documentation.md</code></td>
                    <td>Tài liệu API đầy đủ bằng tiếng Việt (SRS companion)</td>
                  </tr>
                  <tr>
                    <td><code>docs/brainstorm_api_documentation.md</code></td>
                    <td>Brainstorm & yêu cầu tạo tài liệu API</td>
                  </tr>
                  <tr>
                    <td><code>PROJECT_OVERVIEW.md</code></td>
                    <td>Overview kỹ thuật (models, stack, deployment)</td>
                  </tr>
                  <tr>
                    <td><code>MIGRATION_POST_TO_PLACE_PLAN.md</code></td>
                    <td>Kế hoạch migration Post → Place</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- ov-deployment -->
          <section id="ov-deployment" class="doc-section">
            <h3 class="section-title"><i class="fas fa-server"></i> Deployment</h3>
            <div class="code-block-wrapper">
              <div class="code-block-header">
                <span class="code-lang">Commands</span>
              </div>
              <pre class="code-pre"><code># Chạy local
uwsgi --http :5000 --wsgi-file admin_api.py --callable app   # Admin
uwsgi --http :5001 --wsgi-file public_api.py --callable app  # Public

# Migration DB
alembic upgrade head

# Docker
docker-compose up</code></pre>
            </div>
          </section>
        </div>

        <!-- ==================== DOCUMENT 2: DETAILED API REFERENCE ==================== -->
        <div v-else class="document-view">
          <div class="doc-header">
            <h1 class="main-doc-title">Tài Liệu API Hệ Thống KGVH Hưng Yên</h1>
            <div class="doc-meta">
              <span class="meta-tag"><i class="fas fa-code-branch"></i> Phiên bản: 1.0</span>
              <span class="meta-tag"><i class="far fa-calendar-alt"></i> Ngày cập nhật: 2026-06-09</span>
              <span class="meta-tag"><i class="fas fa-check-circle text-primary"></i> Trạng thái: Chính thức</span>
            </div>
          </div>

          <hr class="doc-divider" />

          <!-- dt-overview -->
          <section id="dt-overview" class="doc-section">
            <h3 class="section-title">1. Tổng Quan Hệ Thống</h3>
            <p>
              KGVH Hưng Yên là hệ thống backend phục vụ nền tảng hướng dẫn du lịch của tỉnh Hưng Yên. Hệ thống cung cấp <strong>hai API độc lập</strong>:
            </p>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>API</th>
                    <th>Mục đích</th>
                    <th>Đối tượng sử dụng</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Admin API</strong></td>
                    <td>Quản trị dữ liệu, nội dung, tổ chức</td>
                    <td>Backend staff, nhà quản trị</td>
                  </tr>
                  <tr>
                    <td><strong>Public API</strong></td>
                    <td>Phục vụ ứng dụng mobile/web</td>
                    <td>Người dùng cuối, ứng dụng client</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h5>Kiến trúc công nghệ:</h5>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Thành phần</th>
                    <th>Công nghệ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Ngôn ngữ</td>
                    <td>Python 3.8.10</td>
                  </tr>
                  <tr>
                    <td>Framework</td>
                    <td>Flask 2.2.2</td>
                  </tr>
                  <tr>
                    <td>Cơ sở dữ liệu</td>
                    <td>PostgreSQL + PostGIS</td>
                  </tr>
                  <tr>
                    <td>Cache</td>
                    <td>Redis</td>
                  </tr>
                  <tr>
                    <td>Tìm kiếm</td>
                    <td>Elasticsearch</td>
                  </tr>
                  <tr>
                    <td>Xác thực</td>
                    <td>JWT (HS256) + OAuth2</td>
                  </tr>
                  <tr>
                    <td>Lưu trữ file</td>
                    <td>Google Cloud Storage / S3 VCCloud</td>
                  </tr>
                  <tr>
                    <td>Thanh toán</td>
                    <td>OnePay</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- dt-urls -->
          <section id="dt-urls" class="doc-section">
            <h3 class="section-title">2. Base URL & Môi Trường</h3>
            <h5>Production</h5>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>API</th>
                    <th>Base URL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Admin API</td>
                    <td><code class="text-secondary">https://admin.hy.khonggianvanhoa.vn/api/admin</code></td>
                  </tr>
                  <tr>
                    <td>Public API</td>
                    <td><code class="text-secondary">https://admin.hy.khonggianvanhoa.vn/api/public</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h5>Staging</h5>
            <p>Địa chỉ môi trường staging được cung cấp theo yêu cầu. Liên hệ đội kỹ thuật để nhận thông tin kết nối staging.</p>

            <h5>Header bắt buộc:</h5>
            <div class="code-block-wrapper">
              <pre class="code-pre"><code>Content-Type: application/json

# Cho API cần xác thực:
Authorization: Bearer &lt;access_token&gt;

# Khi user thuộc tổ chức (Public API):
X-Organization-Code: &lt;organization_code&gt;

# Cho app mobile (Public API):
Device-Id: &lt;unique_device_identifier&gt;</code></pre>
            </div>
          </section>

          <!-- dt-auth -->
          <section id="dt-auth" class="doc-section">
            <h3 class="section-title">3. Cơ Chế Xác Thực</h3>

            <h4>3.1 JWT Bearer Token</h4>
            <p>Hệ thống sử dụng <strong>JSON Web Token (JWT)</strong> với thuật toán ký <strong>HS256</strong>.</p>

            <h5>Luồng xác thực:</h5>
            <pre class="ascii-flow"><code>Client                          Server
  │                               │
  │── POST /auth (email+password) ─►│
  │◄── { access_token, auth_user } ─│
  │                               │
  │── GET /resource               │
  │   Authorization: Bearer <token>─►│
  │◄── 200 OK { data }           │</token></code></pre>

            <ul class="styled-list mt-3">
              <li><strong>Thời hạn token:</strong> 2 giờ (7200 giây) kể từ thời điểm cấp phát.</li>
            </ul>

            <h5>Cấu trúc JWT payload:</h5>
            <div class="code-block-wrapper">
              <pre class="code-pre"><code>{
  "id": "&lt;user_uuid&gt;",
  "exp": "&lt;unix_timestamp&gt;"
}</code></pre>
            </div>

            <h5>Gia hạn token (Public API):</h5>
            <div class="table-container mb-4">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>POST /create-refresh-token</code></td>
                    <td>Tạo refresh token mới</td>
                  </tr>
                  <tr>
                    <td><code>POST /refresh-token</code></td>
                    <td>Đổi refresh token lấy access token mới</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>3.2 OAuth2 — Đăng nhập mạng xã hội (Public API)</h4>
            <div class="table-container mb-4">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Nhà cung cấp</th>
                    <th>Endpoint</th>
                    <th>Tham số đầu vào</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Facebook</td>
                    <td><code>POST /facebook-auth</code></td>
                    <td><code>access_token</code> (Facebook OAuth token)</td>
                  </tr>
                  <tr>
                    <td>Google</td>
                    <td><code>POST /google-auth</code></td>
                    <td><code>id_token</code> (Google ID token)</td>
                  </tr>
                  <tr>
                    <td>Apple</td>
                    <td><code>POST /apple-auth</code></td>
                    <td><code>identity_token</code> (Apple identity token)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>3.3 Giới hạn đăng nhập (Public API)</h4>
            <ul class="styled-list">
              <li>Mặc định: tối đa <strong>3 lần</strong> đăng nhập sai liên tiếp</li>
              <li>Khi vượt quá: tài khoản bị khóa tạm thời</li>
              <li>Cấu hình qua biến môi trường <code>LOGIN_MAX_ATTEMPT</code></li>
            </ul>
          </section>

          <!-- dt-roles -->
          <section id="dt-roles" class="doc-section">
            <h3 class="section-title">4. Phân Quyền Truy Cập</h3>

            <h4>4.1 Vai trò người dùng</h4>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Mô tả</th>
                    <th>Phạm vi</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>administrator</code></td>
                    <td>Quản trị viên cấp cao</td>
                    <td>Toàn quyền Admin API</td>
                  </tr>
                  <tr>
                    <td><code>editor</code></td>
                    <td>Biên tập viên nội dung</td>
                    <td>Admin API — quản lý content</td>
                  </tr>
                  <tr>
                    <td><code>moderator</code></td>
                    <td>Quản lý tổ chức</td>
                    <td>Admin API — giới hạn theo tổ chức (<code>X-Organization-Code</code>)</td>
                  </tr>
                  <tr>
                    <td><code>user</code></td>
                    <td>Người dùng thường</td>
                    <td>Public API</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>4.2 Quy tắc phân quyền Admin API</h4>
            <ul class="styled-list">
              <li>Tất cả endpoint Admin API đều <strong>yêu cầu xác thực</strong> (trừ <code>POST /auth</code>).</li>
              <li>Người dùng phải có <code>user_type = 'staff'</code>.</li>
              <li>Chỉ các role <code>administrator</code>, <code>editor</code>, <code>moderator</code> được phép truy cập.</li>
              <li>Role <code>moderator</code> <strong>bắt buộc</strong> gửi kèm header <code>X-Organization-Code</code>; nếu thiếu, hệ thống tự dùng tổ chức đầu tiên trong danh sách.</li>
              <li>IAM (Identity and Access Management): kiểm soát quyền chi tiết hơn theo từng <code>Action</code> và <code>ManagedResource</code> trong module IAM.</li>
            </ul>

            <h4>4.3 Quy tắc phân quyền Public API</h4>
            <ul class="styled-list">
              <li>Nhiều endpoint là <strong>public</strong> (không cần đăng nhập): danh sách địa điểm, bài viết, tour, tìm kiếm.</li>
              <li>Các tính năng cá nhân <strong>yêu cầu đăng nhập</strong>: profile, giỏ hàng, đặt hàng, bình luận, yêu thích, đánh giá.</li>
              <li>Header <code>Device-Id</code> bắt buộc cho các thao tác liên quan đến thiết bị.</li>
            </ul>

            <h4>4.4 Module IAM (Admin API)</h4>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>Action</code></td>
                    <td>Định nghĩa các quyền hành động</td>
                  </tr>
                  <tr>
                    <td><code>ManagedResource</code></td>
                    <td>Tài nguyên được quản lý</td>
                  </tr>
                  <tr>
                    <td><code>OrganizationAction</code></td>
                    <td>Gán quyền theo tổ chức</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>Endpoint quản lý IAM:</p>
            <ul class="styled-list">
              <li><code>GET/POST /iam/action</code></li>
              <li><code>GET/POST /iam/managed-resource</code></li>
              <li><code>GET/POST /iam/org-action</code></li>
              <li><code>GET/POST /iam/org-action/assignee</code></li>
            </ul>
          </section>

          <!-- dt-errors -->
          <section id="dt-errors" class="doc-section">
            <h3 class="section-title">5. Mã Lỗi Hệ Thống</h3>

            <h4>5.1 HTTP Status Code</h4>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>HTTP Status</th>
                    <th>Tên lỗi</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>200</code></td>
                    <td>OK</td>
                    <td>Thành công</td>
                  </tr>
                  <tr>
                    <td><code>400</code></td>
                    <td>Bad Request</td>
                    <td>Tham số đầu vào không hợp lệ</td>
                  </tr>
                  <tr>
                    <td><code>401</code></td>
                    <td>Unauthorized</td>
                    <td>Không có token hoặc token không hợp lệ</td>
                  </tr>
                  <tr>
                    <td><code>403</code></td>
                    <td>Forbidden</td>
                    <td>Không có quyền truy cập</td>
                  </tr>
                  <tr>
                    <td><code>405</code></td>
                    <td>Method Not Allowed</td>
                    <td>HTTP method không được hỗ trợ</td>
                  </tr>
                  <tr>
                    <td><code>422</code></td>
                    <td>Unprocessable Entity</td>
                    <td>Dữ liệu hợp lệ nhưng không thể xử lý</td>
                  </tr>
                  <tr>
                    <td><code>500</code></td>
                    <td>Internal Server Error</td>
                    <td>Lỗi phía server</td>
                  </tr>
                  <tr>
                    <td><code>503</code></td>
                    <td>Service Unavailable</td>
                    <td>Dịch vụ phụ thuộc không khả dụng</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>5.2 Cấu trúc Response lỗi</h4>
            <div class="code-block-wrapper">
              <pre class="code-pre"><code>{
  "message": "Mô tả lỗi bằng tiếng Anh",
  "error": "ErrorCodeString",
  "payload": "Thông tin bổ sung (tùy chọn)"
}</code></pre>
            </div>

            <h4>5.3 Error Code phổ biến</h4>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Error Code</th>
                    <th>HTTP Status</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>InvalidCredential</code></td>
                    <td>400</td>
                    <td>Email hoặc mật khẩu sai</td>
                  </tr>
                  <tr>
                    <td><code>AccountHasBeenLocked</code></td>
                    <td>401</td>
                    <td>Tài khoản bị khóa</td>
                  </tr>
                  <tr>
                    <td><code>MissingDeviceInformation</code></td>
                    <td>400</td>
                    <td>Thiếu header <code>Device-Id</code></td>
                  </tr>
                  <tr>
                    <td><code>Unauthorized</code></td>
                    <td>401</td>
                    <td>Token không hợp lệ hoặc hết hạn</td>
                  </tr>
                  <tr>
                    <td><code>PermissionError</code></td>
                    <td>403</td>
                    <td>Không đủ quyền</td>
                  </tr>
                  <tr>
                    <td><code>BadRequestParams</code></td>
                    <td>400</td>
                    <td>Tham số không hợp lệ</td>
                  </tr>
                  <tr>
                    <td><code>UnprocessedEntity</code></td>
                    <td>422</td>
                    <td>Không thể xử lý entity</td>
                  </tr>
                  <tr>
                    <td><code>ServiceNotAvailable</code></td>
                    <td>503</td>
                    <td>Dịch vụ không khả dụng</td>
                  </tr>
                  <tr>
                    <td><code>ServerError</code></td>
                    <td>500</td>
                    <td>Lỗi server nội bộ</td>
                  </tr>
                  <tr>
                    <td><code>MethodNotAllowed</code></td>
                    <td>405</td>
                    <td>Method không được cho phép</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- dt-sync -->
          <section id="dt-sync" class="doc-section">
            <h3 class="section-title">6. Quy Tắc Đồng Bộ Dữ Liệu</h3>

            <h4>6.1 Đồng bộ Elasticsearch</h4>
            <p>Dữ liệu tìm kiếm được đồng bộ qua hai cơ chế:</p>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Cơ chế</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>RQ Worker</strong> (Redis Queue)</td>
                    <td>Đồng bộ bất đồng bộ sau khi tạo/cập nhật dữ liệu</td>
                  </tr>
                  <tr>
                    <td><strong>RabbitMQ Consumer</strong></td>
                    <td>Event-driven sync khi có sự kiện từ hệ thống</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p><strong>Dữ liệu được index:</strong> Place, Tour, Artifact, Post.</p>
            <p class="text-warning"><i class="fas fa-exclamation-triangle"></i> <strong>Lưu ý:</strong> Sau khi tạo/cập nhật dữ liệu qua Admin API, có thể có <strong>độ trễ ngắn</strong> (vài giây) trước khi kết quả tìm kiếm cập nhật.</p>

            <h4>6.2 Cache Redis</h4>
            <div class="table-container mb-4">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Dữ liệu</th>
                    <th>TTL</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Session đăng nhập</td>
                    <td>Phiên làm việc</td>
                    <td>Trạng thái đăng nhập và thiết bị</td>
                  </tr>
                  <tr>
                    <td>Login attempt counter</td>
                    <td>Tự reset</td>
                    <td>Đếm số lần đăng nhập sai</td>
                  </tr>
                  <tr>
                    <td>Access token</td>
                    <td>2 giờ</td>
                    <td>Token xác thực người dùng</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>6.3 Trạng thái nội dung</h4>
            <p>Nội dung trong hệ thống có các trạng thái:</p>

            <h5>Place:</h5>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Trạng thái</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>private</code></td>
                    <td>Nội bộ, không hiển thị Public API</td>
                  </tr>
                  <tr>
                    <td><code>public</code></td>
                    <td>Công khai, hiển thị trên Public API</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h5>Tour / Post:</h5>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Trạng thái</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>draft</code></td>
                    <td>Bản nháp</td>
                  </tr>
                  <tr>
                    <td><code>public</code></td>
                    <td>Công khai</td>
                  </tr>
                  <tr>
                    <td><code>private</code></td>
                    <td>Ẩn</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h5>Campaign:</h5>
            <div class="table-container mb-4">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Trạng thái</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>active</code></td>
                    <td>Đang chạy</td>
                  </tr>
                  <tr>
                    <td><code>inactive</code></td>
                    <td>Không hoạt động</td>
                  </tr>
                  <tr>
                    <td><code>expired</code></td>
                    <td>Hết hạn</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>6.4 Background Jobs</h4>
            <div class="table-container mb-4">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Mô tả</th>
                    <th>Tần suất</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>CodeCounting</code></td>
                    <td>Theo dõi lượt dùng mã khuyến mãi</td>
                    <td>Định kỳ</td>
                  </tr>
                  <tr>
                    <td><code>CampaignStatusChecker</code></td>
                    <td>Tự động cập nhật trạng thái campaign</td>
                    <td>Định kỳ</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>6.5 Đa ngôn ngữ (i18n)</h4>
            <ul class="styled-list">
              <li>Hệ thống hỗ trợ <strong>4 ngôn ngữ</strong>: <code>vi</code> (Tiếng Việt), <code>en</code> (Tiếng Anh), <code>zh</code> (Tiếng Trung), <code>ko</code> (Tiếng Hàn).</li>
              <li>Tham số <code>locale</code> cần được truyền trong request để nhận nội dung đúng ngôn ngữ.</li>
              <li>Nội dung dịch được lưu trong các bảng <code>*Translation</code> riêng biệt.</li>
              <li>Nếu không có bản dịch cho ngôn ngữ yêu cầu, hệ thống trả về bản gốc.</li>
            </ul>
          </section>

          <!-- dt-common -->
          <section id="dt-common" class="doc-section">
            <h3 class="section-title">7. Chuẩn Dữ Liệu Chung</h3>

            <h4>7.1 Định dạng ID</h4>
            <ul class="styled-list">
              <li>Tất cả ID sử dụng định dạng <strong>UUID v4</strong> (36 ký tự).</li>
              <li>Ví dụ: <code>"550e8400-e29b-41d4-a716-446655440000"</code></li>
            </ul>

            <h4>7.2 Phân trang</h4>
            <p>Các endpoint trả về danh sách hỗ trợ phân trang qua query params:</p>
            <div class="table-container mb-3">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Tham số</th>
                    <th>Kiểu</th>
                    <th>Mặc định</th>
                    <th>Mô tả</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>page</code></td>
                    <td>integer</td>
                    <td><code>1</code></td>
                    <td>Trang hiện tại</td>
                  </tr>
                  <tr>
                    <td><code>per_page</code></td>
                    <td>integer</td>
                    <td><code>50</code></td>
                    <td>Số kết quả mỗi trang</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h5>Cấu trúc response danh sách:</h5>
            <div class="code-block-wrapper mb-4">
              <pre class="code-pre"><code>{
  "items": [...],
  "total": 120,
  "page": 1,
  "per_page": 50
}</code></pre>
            </div>

            <h4>7.3 Định dạng thời gian</h4>
            <ul class="styled-list">
              <li>Sử dụng chuẩn <strong>ISO 8601</strong>: <code>YYYY-MM-DDTHH:MM:SS</code></li>
              <li>Ví dụ: <code>"2024-01-15T08:30:00"</code></li>
            </ul>

            <h4>7.4 Tọa độ địa lý</h4>
            <ul class="styled-list">
              <li>Sử dụng chuẩn <strong>WGS84</strong> (latitude/longitude)</li>
              <li><code>latitude</code>: số thực, ví dụ <code>21.0285</code></li>
              <li><code>longitude</code>: số thực, ví dụ <code>105.8542</code></li>
              <li><code>distance_box</code>: <code>[min_lat, min_lng, max_lat, max_lng]</code> — tìm kiếm trong vùng bounding box</li>
            </ul>

            <h4>7.5 Giới hạn độ dài chuỗi</h4>
            <div class="table-container">
              <table class="api-table">
                <thead>
                  <tr>
                    <th>Loại</th>
                    <th>Giới hạn</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>EX_SHORT</td>
                    <td>50 ký tự</td>
                  </tr>
                  <tr>
                    <td>SHORT</td>
                    <td>100 ký tự</td>
                  </tr>
                  <tr>
                    <td>MEDIUM</td>
                    <td>500 ký tự</td>
                  </tr>
                  <tr>
                    <td>LONG</td>
                    <td>2000 ký tự</td>
                  </tr>
                  <tr>
                    <td>EX_LONG</td>
                    <td>(lớn)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- dt-admin -->
          <section id="dt-admin" class="doc-section">
            <h3 class="section-title">8. Admin API — Danh Sách Endpoint</h3>
            <div class="meta-card mb-4">
              <span class="meta-label">Base URL</span>
              <span class="meta-value code-text text-secondary">https://admin.hy.khonggianvanhoa.vn/api/admin</span>
              <p class="mt-2 mb-0 text-muted-sub"><strong>Xác thực:</strong> Tất cả endpoint yêu cầu <code>Authorization: Bearer &lt;token&gt;</code> trừ khi ghi chú khác.</p>
            </div>

            <div v-for="section in adminSections" :id="section.id" :key="section.id" class="sub-section-card">
              <h4 class="sub-section-title">{{ section.title }}</h4>

              <div v-if="section.id === 'dt-admin-8-1'" class="section-extra-details mb-3">
                <h5>POST /auth — Request:</h5>
                <div class="code-block-wrapper mb-2">
                  <pre class="code-pre"><code>{
  "email": "admin@example.com",
  "password": "your_password"
}</code></pre>
                </div>
                <h5>POST /auth — Response:</h5>
                <div class="code-block-wrapper">
                  <pre class="code-pre"><code>{
  "auth_user": {
    "id": "uuid",
    "email": "admin@example.com",
    "name": "Admin Name",
    "staff_type": "administrator",
    "organizations": [...]
  },
  "access_token": "eyJ..."
}</code></pre>
                </div>
              </div>

              <div v-if="section.id === 'dt-admin-8-2'" class="section-extra-details mb-3">
                <h5>Query params GET /user/list:</h5>
                <div class="table-container mb-2">
                  <table class="api-table">
                    <thead>
                      <tr>
                        <th>Tham số</th>
                        <th>Kiểu</th>
                        <th>Mô tả</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>page</code></td>
                        <td>integer</td>
                        <td>Số trang</td>
                      </tr>
                      <tr>
                        <td><code>per_page</code></td>
                        <td>integer</td>
                        <td>Kết quả/trang</td>
                      </tr>
                      <tr>
                        <td><code>search_text</code></td>
                        <td>string</td>
                        <td>Tìm kiếm theo tên/email</td>
                      </tr>
                      <tr>
                        <td><code>user_type</code></td>
                        <td>string</td>
                        <td>Lọc theo loại tài khoản</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="section.id === 'dt-admin-8-4'" class="section-extra-details mb-3">
                <h5>Query params GET /place/list:</h5>
                <div class="table-container mb-2">
                  <table class="api-table">
                    <thead>
                      <tr>
                        <th>Tham số</th>
                        <th>Kiểu</th>
                        <th>Mô tả</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>locale</code></td>
                        <td>string</td>
                        <td>Ngôn ngữ: <code>vi</code>, <code>en</code>, <code>zh</code>, <code>ko</code></td>
                      </tr>
                      <tr>
                        <td><code>page</code></td>
                        <td>integer</td>
                        <td>Số trang</td>
                      </tr>
                      <tr>
                        <td><code>per_page</code></td>
                        <td>integer</td>
                        <td>Kết quả/trang</td>
                      </tr>
                      <tr>
                        <td><code>search_text</code></td>
                        <td>string</td>
                        <td>Tìm kiếm theo tên</td>
                      </tr>
                      <tr>
                        <td><code>place_types</code></td>
                        <td>list</td>
                        <td>Lọc loại: <code>area</code>, <code>artifact</code>, <code>image-library</code>, <code>document-library</code>, <code>sound-library</code>, <code>video-library</code>, <code>post</code></td>
                      </tr>
                      <tr>
                        <td><code>statuses</code></td>
                        <td>list</td>
                        <td>Lọc trạng thái: <code>public</code>, <code>private</code></td>
                      </tr>
                      <tr>
                        <td><code>category_ids</code></td>
                        <td>list</td>
                        <td>Lọc theo danh mục</td>
                      </tr>
                      <tr>
                        <td><code>parent_id</code></td>
                        <td>uuid</td>
                        <td>Lọc theo địa điểm cha</td>
                      </tr>
                      <tr>
                        <td><code>area_id</code></td>
                        <td>uuid</td>
                        <td>Lọc theo khu vực</td>
                      </tr>
                      <tr>
                        <td><code>latitude</code> / <code>longitude</code> / <code>r</code></td>
                        <td>float</td>
                        <td>Tìm kiếm theo bán kính (km)</td>
                      </tr>
                      <tr>
                        <td><code>distance_box</code></td>
                        <td>list</td>
                        <td>Bounding box <code>[min_lat, min_lng, max_lat, max_lng]</code></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="table-container">
                <table class="api-table">
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Endpoint</th>
                      <th>Mô tả</th>
                      <th>Thử nghiệm</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ep in section.endpoints" :key="ep.path + '-' + ep.method">
                      <td><span :class="['method-badge', ep.method.toLowerCase()]">{{ ep.method }}</span></td>
                      <td><code>{{ ep.path }}</code></td>
                      <td>{{ ep.desc }}</td>
                      <td>
                        <button class="try-btn-sm" @click="tryEndpoint('admin', ep.path)">
                          <i class="fas fa-play"></i> Thử
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <!-- dt-public -->
          <section id="dt-public" class="doc-section">
            <h3 class="section-title">9. Public API — Danh Sách Endpoint</h3>
            <div class="meta-card mb-4">
              <span class="meta-label">Base URL</span>
              <span class="meta-value code-text text-secondary">https://admin.hy.khonggianvanhoa.vn/api/public</span>
              <p class="mt-2 mb-0 text-muted-sub"><strong>Ghi chú:</strong> Các endpoint đánh dấu <code>[Auth]</code> yêu cầu header <code>Authorization: Bearer &lt;token&gt;</code>.</p>
            </div>

            <div v-for="section in publicSections" :id="section.id" :key="section.id" class="sub-section-card">
              <h4 class="sub-section-title">{{ section.title }}</h4>

              <div v-if="section.id === 'dt-public-9-1'" class="section-extra-details mb-3">
                <h5>POST /auth — Request:</h5>
                <div class="code-block-wrapper mb-2">
                  <pre class="code-pre"><code>{
  "email": "user@example.com",
  "password": "your_password"
}</code></pre>
                </div>
                <h5>Headers bắt buộc cho /auth:</h5>
                <div class="code-block-wrapper mb-2">
                  <pre class="code-pre"><code>Device-Id: &lt;unique_device_id&gt;</code></pre>
                </div>
                <h5>POST /register — Request:</h5>
                <div class="code-block-wrapper">
                  <pre class="code-pre"><code>{
  "email": "user@example.com",
  "name": "Nguyen Van A",
  "phone": "0901234567",
  "avatar_url": "https://...",
  "password": "your_password"
}</code></pre>
                </div>
              </div>

              <div v-if="section.id === 'dt-public-9-3'" class="section-extra-details mb-3">
                <h5>Query params GET /place:</h5>
                <div class="table-container mb-3">
                  <table class="api-table">
                    <thead>
                      <tr>
                        <th>Tham số</th>
                        <th>Kiểu</th>
                        <th>Bắt buộc</th>
                        <th>Mô tả</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>id</code></td>
                        <td>uuid</td>
                        <td>Có</td>
                        <td>ID địa điểm</td>
                      </tr>
                      <tr>
                        <td><code>locale</code></td>
                        <td>string</td>
                        <td>Có</td>
                        <td>Ngôn ngữ: <code>vi</code>, <code>en</code>, <code>zh</code>, <code>ko</code></td>
                      </tr>
                      <tr>
                        <td><code>only_fields</code></td>
                        <td>list</td>
                        <td>Không</td>
                        <td>Lọc chỉ lấy các field cần thiết</td>
                      </tr>
                      <tr>
                        <td><code>fetch_tour_count</code></td>
                        <td>boolean</td>
                        <td>Không</td>
                        <td>Kèm số lượng tour</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h5>Query params GET /place/list:</h5>
                <div class="table-container">
                  <table class="api-table">
                    <thead>
                      <tr>
                        <th>Tham số</th>
                        <th>Kiểu</th>
                        <th>Bắt buộc</th>
                        <th>Mô tả</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>locale</code></td>
                        <td>string</td>
                        <td>Có</td>
                        <td>Ngôn ngữ</td>
                      </tr>
                      <tr>
                        <td><code>page</code></td>
                        <td>integer</td>
                        <td>Không</td>
                        <td>Trang, mặc định 1</td>
                      </tr>
                      <tr>
                        <td><code>per_page</code></td>
                        <td>integer</td>
                        <td>Không</td>
                        <td>Kết quả/trang, mặc định 50</td>
                      </tr>
                      <tr>
                        <td><code>place_types</code></td>
                        <td>list</td>
                        <td>Không</td>
                        <td><code>area</code>, <code>country</code>, <code>attraction</code>, <code>entertainment</code>, <code>cuisine</code></td>
                      </tr>
                      <tr>
                        <td><code>statuses</code></td>
                        <td>list</td>
                        <td>Không</td>
                        <td><code>public</code>, <code>private</code></td>
                      </tr>
                      <tr>
                        <td><code>search_text</code></td>
                        <td>string</td>
                        <td>Không</td>
                        <td>Tìm kiếm tên</td>
                      </tr>
                      <tr>
                        <td><code>category_ids</code></td>
                        <td>list uuid</td>
                        <td>Không</td>
                        <td>Lọc theo danh mục</td>
                      </tr>
                      <tr>
                        <td><code>parent_id</code></td>
                        <td>uuid</td>
                        <td>Không</td>
                        <td>Lọc theo địa điểm cha</td>
                      </tr>
                      <tr>
                        <td><code>area_id</code></td>
                        <td>uuid</td>
                        <td>Không</td>
                        <td>Lọc theo khu vực</td>
                      </tr>
                      <tr>
                        <td><code>country_id</code></td>
                        <td>uuid</td>
                        <td>Không</td>
                        <td>Lọc theo quốc gia</td>
                      </tr>
                      <tr>
                        <td><code>latitude</code> / <code>longitude</code></td>
                        <td>float</td>
                        <td>Không</td>
                        <td>Tọa độ trung tâm tìm kiếm</td>
                      </tr>
                      <tr>
                        <td><code>r</code></td>
                        <td>float</td>
                        <td>Không</td>
                        <td>Bán kính tìm kiếm (km)</td>
                      </tr>
                      <tr>
                        <td><code>distance_box</code></td>
                        <td>list float</td>
                        <td>Không</td>
                        <td>Bounding box <code>[min_lat, min_lng, max_lat, max_lng]</code></td>
                      </tr>
                      <tr>
                        <td><code>free</code></td>
                        <td>boolean</td>
                        <td>Không</td>
                        <td>Chỉ lấy địa điểm miễn phí</td>
                      </tr>
                      <tr>
                        <td><code>star</code></td>
                        <td>integer</td>
                        <td>Không</td>
                        <td>Lọc theo số sao</td>
                      </tr>
                      <tr>
                        <td><code>highlighted</code></td>
                        <td>boolean</td>
                        <td>Không</td>
                        <td>Lọc địa điểm nổi bật</td>
                      </tr>
                      <tr>
                        <td><code>from_date</code> / <code>to_date</code></td>
                        <td>datetime</td>
                        <td>Không</td>
                        <td>Lọc theo thời gian hoạt động</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="section.id === 'dt-public-9-7'" class="section-extra-details mb-3">
                <h5>Query params GET /search/text:</h5>
                <div class="table-container">
                  <table class="api-table">
                    <thead>
                      <tr>
                        <th>Tham số</th>
                        <th>Kiểu</th>
                        <th>Bắt buộc</th>
                        <th>Mô tả</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>text</code></td>
                        <td>string</td>
                        <td>Không</td>
                        <td>Từ khóa tìm kiếm</td>
                      </tr>
                      <tr>
                        <td><code>locale</code></td>
                        <td>string</td>
                        <td>Có</td>
                        <td>Ngôn ngữ kết quả</td>
                      </tr>
                      <tr>
                        <td><code>only_parent</code></td>
                        <td>boolean</td>
                        <td>Không</td>
                        <td>Chỉ tìm địa điểm cha</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="section.id === 'dt-public-9-14'" class="section-extra-details mb-3">
                <h5>POST /storage — Form Data:</h5>
                <div class="table-container mb-2">
                  <table class="api-table">
                    <thead>
                      <tr>
                        <th>Tham số</th>
                        <th>Kiểu</th>
                        <th>Bắt buộc</th>
                        <th>Mô tả</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>file</code></td>
                        <td>file</td>
                        <td>Có</td>
                        <td>File cần upload</td>
                      </tr>
                      <tr>
                        <td><code>file_type</code></td>
                        <td>string</td>
                        <td>Có</td>
                        <td>Loại: <code>image</code>, <code>audio</code>, <code>video</code>, <code>document</code></td>
                      </tr>
                      <tr>
                        <td><code>object_id</code></td>
                        <td>uuid</td>
                        <td>Không</td>
                        <td>ID đối tượng gắn kèm</td>
                      </tr>
                      <tr>
                        <td><code>object_type</code></td>
                        <td>string</td>
                        <td>Không</td>
                        <td>Loại đối tượng: <code>place</code>, <code>post</code>, <code>tour</code>, <code>artifact</code></td>
                      </tr>
                      <tr>
                        <td><code>locale</code></td>
                        <td>string</td>
                        <td>Không</td>
                        <td>Ngôn ngữ</td>
                      </tr>
                      <tr>
                        <td><code>title</code></td>
                        <td>string</td>
                        <td>Không</td>
                        <td>Tiêu đề file</td>
                      </tr>
                      <tr>
                        <td><code>is_avatar</code></td>
                        <td>boolean</td>
                        <td>Không</td>
                        <td>Đặt làm ảnh đại diện</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p><strong>Giới hạn kích thước:</strong></p>
                <ul class="styled-list">
                  <li>Public API: tối đa <strong>1 MB</strong></li>
                  <li>Admin API: tối đa <strong>5 GB</strong></li>
                </ul>
              </div>

              <div class="table-container">
                <table class="api-table">
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Endpoint</th>
                      <th>Mô tả</th>
                      <th>Thử nghiệm</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ep in section.endpoints" :key="ep.path + '-' + ep.method">
                      <td><span :class="['method-badge', ep.method.toLowerCase()]">{{ ep.method }}</span></td>
                      <td><code>{{ ep.path }}</code></td>
                      <td>{{ ep.desc }}</td>
                      <td>
                        <button class="try-btn-sm" @click="tryEndpoint('public', ep.path)">
                          <i class="fas fa-play"></i> Thử
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <!-- dt-examples -->
          <section id="dt-examples" class="doc-section">
            <h3 class="section-title">10. Ví Dụ Phản Hồi Mẫu</h3>
            <!-- 10.1 -->
            <div class="example-card mb-4">
              <h4 class="example-title">10.1 Đăng nhập thành công (Public API)</h4>
              <div class="tabs-example">
                <button :class="['tab-ex-btn', { active: exampleTabs['10-1'] === 'req' }]" @click="exampleTabs['10-1'] = 'req'">Request</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-1'] === 'res200' }]" @click="exampleTabs['10-1'] = 'res200'">Response 200</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-1'] === 'res400_invalid' }]" @click="exampleTabs['10-1'] = 'res400_invalid'">Response 400 (Invalid)</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-1'] === 'res400_exceed' }]" @click="exampleTabs['10-1'] = 'res400_exceed'">Response 400 (Exceeded)</button>
              </div>
              <div class="code-block-wrapper">
                <pre v-if="exampleTabs['10-1'] === 'req'" class="code-pre"><code>POST https://admin.hy.khonggianvanhoa.vn/api/public/auth
Content-Type: application/json
Device-Id: device-abc-123

{
  "email": "user@example.com",
  "password": "password123"
}</code></pre>
                <pre v-else-if="exampleTabs['10-1'] === 'res200'" class="code-pre"><code>{
  "auth_user": {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "email": "user@example.com",
    "name": "Nguyen Van A",
    "phone": "0901234567",
    "avatar_url": "https://storage.example.com/avatar.jpg",
    "user_type": "user",
    "likes": [],
    "rates": []
  },
  "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
}</code></pre>
                <pre v-else-if="exampleTabs['10-1'] === 'res400_invalid'" class="code-pre"><code>{
  "error": "InvalidCredential",
  "message": "Email or password wrong"
}</code></pre>
                <pre v-else-if="exampleTabs['10-1'] === 'res400_exceed'" class="code-pre"><code>{
  "error": "Login exceed max attempt 3",
  "message": "Bad request params."
}</code></pre>
              </div>
            </div>

            <!-- 10.2 -->
            <div class="example-card mb-4">
              <h4 class="example-title">10.2 Chi tiết địa điểm</h4>
              <div class="tabs-example">
                <button :class="['tab-ex-btn', { active: exampleTabs['10-2'] === 'req' }]" @click="exampleTabs['10-2'] = 'req'">Request</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-2'] === 'res200' }]" @click="exampleTabs['10-2'] = 'res200'">Response 200</button>
              </div>
              <div class="code-block-wrapper">
                <pre v-if="exampleTabs['10-2'] === 'req'" class="code-pre"><code>GET https://admin.hy.khonggianvanhoa.vn/api/public/place?id=&lt;uuid&gt;&amp;locale=vi</code></pre>
                <pre v-else-if="exampleTabs['10-2'] === 'res200'" class="code-pre"><code>{
  "id": "550e8400-e29b-41d4-a716-446655440000",
  "name": "Hồ Hoàn Kiếm",
  "slug": "ho-hoan-kiem",
  "place_type": "attraction",
  "status": "public",
  "location": {
    "type": "Point",
    "coordinates": [105.8514, 21.0285]
  },
  "description": "Hồ Hoàn Kiếm là một hồ nước ngọt nằm ở trung tâm thành phố Hà Nội...",
  "contact_info": {
    "email": null,
    "website": "https://example.com",
    "phone": "024 1234 5678"
  },
  "opening_time": {
    "open_time": "06:00",
    "close_time": "22:00"
  },
  "price_info": {
    "free": true,
    "minimum": 0,
    "maximum": 0
  },
  "category": {
    "id": "uuid",
    "name": "Di tích lịch sử"
  },
  "tags": [
    { "id": "uuid", "name": "Hà Nội" },
    { "id": "uuid", "name": "Di tích" }
  ],
  "avatar": {
    "id": "uuid",
    "url": "https://storage.example.com/place/hoan-kiem.jpg",
    "file_type": "image"
  },
  "likes": [],
  "rates": [],
  "is_favorited": false,
  "children_count": 3,
  "tour_count": 5,
  "qr_code": "https://qr.example.com/abc123",
  "translation": {
    "id": "uuid",
    "locale": "vi",
    "name": "Hồ Hoàn Kiếm",
    "slug": "ho-hoan-kiem"
  },
  "translations": [
    { "id": "uuid", "locale": "vi", "name": "Hồ Hoàn Kiếm", "slug": "ho-hoan-kiem" },
    { "id": "uuid", "locale": "en", "name": "Hoan Kiem Lake", "slug": "hoan-kiem-lake" }
  ]
}</code></pre>
              </div>
            </div>

            <!-- 10.3 -->
            <div class="example-card mb-4">
              <h4 class="example-title">10.3 Danh sách địa điểm (phân trang)</h4>
              <div class="tabs-example">
                <button :class="['tab-ex-btn', { active: exampleTabs['10-3'] === 'req' }]" @click="exampleTabs['10-3'] = 'req'">Request</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-3'] === 'res200' }]" @click="exampleTabs['10-3'] = 'res200'">Response 200</button>
              </div>
              <div class="code-block-wrapper">
                <pre v-if="exampleTabs['10-3'] === 'req'" class="code-pre"><code>GET https://admin.hy.khonggianvanhoa.vn/api/public/place/list?locale=vi&amp;place_types=attraction&amp;page=1&amp;per_page=10</code></pre>
                <pre v-else-if="exampleTabs['10-3'] === 'res200'" class="code-pre"><code>{
  "items": [
    {
      "id": "uuid1",
      "name": "Hồ Hoàn Kiếm",
      "place_type": "attraction",
      "status": "public",
      "slug": "ho-hoan-kiem",
      "avatar": { "url": "https://..." }
    }
  ],
  "total": 120,
  "page": 1,
  "per_page": 10
}</code></pre>
              </div>
            </div>

            <!-- 10.4 -->
            <div class="example-card mb-4">
              <h4 class="example-title">10.4 Tìm kiếm</h4>
              <div class="tabs-example">
                <button :class="['tab-ex-btn', { active: exampleTabs['10-4'] === 'req' }]" @click="exampleTabs['10-4'] = 'req'">Request</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-4'] === 'res200' }]" @click="exampleTabs['10-4'] = 'res200'">Response 200</button>
              </div>
              <div class="code-block-wrapper">
                <pre v-if="exampleTabs['10-4'] === 'req'" class="code-pre"><code>GET https://admin.hy.khonggianvanhoa.vn/api/public/search/text?text=Hội An&amp;locale=vi</code></pre>
                <pre v-else-if="exampleTabs['10-4'] === 'res200'" class="code-pre"><code>{
  "places": [
    {
      "id": "uuid",
      "name": "Phố Cổ Hội An",
      "place_type": "attraction",
      "slug": "pho-co-hoi-an"
    }
  ],
  "posts": [],
  "tours": [],
  "artifacts": []
}</code></pre>
              </div>
            </div>

            <!-- 10.5 -->
            <div class="example-card mb-4">
              <h4 class="example-title">10.5 Lỗi xác thực</h4>
              <div class="tabs-example">
                <button :class="['tab-ex-btn', { active: exampleTabs['10-5'] === 'res401' }]" @click="exampleTabs['10-5'] = 'res401'">Response 401</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-5'] === 'res403' }]" @click="exampleTabs['10-5'] = 'res403'">Response 403</button>
              </div>
              <div class="code-block-wrapper">
                <pre v-if="exampleTabs['10-5'] === 'res401'" class="code-pre"><code>{
  "error": "Unauthorized",
  "message": "Unauthorized error."
}</code></pre>
                <pre v-else-if="exampleTabs['10-5'] === 'res403'" class="code-pre"><code>{
  "error": "PermissionError",
  "message": "Permission error."
}</code></pre>
              </div>
            </div>

            <!-- 10.6 -->
            <div class="example-card mb-4">
              <h4 class="example-title">10.6 Upload file</h4>
              <div class="tabs-example">
                <button :class="['tab-ex-btn', { active: exampleTabs['10-6'] === 'req' }]" @click="exampleTabs['10-6'] = 'req'">Request</button>
                <button :class="['tab-ex-btn', { active: exampleTabs['10-6'] === 'res200' }]" @click="exampleTabs['10-6'] = 'res200'">Response 200</button>
              </div>
              <div class="code-block-wrapper">
                <pre v-if="exampleTabs['10-6'] === 'req'" class="code-pre"><code>POST https://admin.hy.khonggianvanhoa.vn/api/public/storage
Authorization: Bearer &lt;token&gt;
Content-Type: multipart/form-data

file=&lt;binary&gt;
file_type=image
object_id=&lt;place_uuid&gt;
object_type=place
is_avatar=true</code></pre>
                <pre v-else-if="exampleTabs['10-6'] === 'res200'" class="code-pre"><code>{
  "id": "uuid",
  "url": "https://storage.vntravelguideapp.com/production/place/avatar.jpg",
  "file_type": "image",
  "object_id": "uuid",
  "object_type": "place",
  "locale": null,
  "title": null,
  "created_at": "2024-01-15T08:30:00"
}</code></pre>
              </div>
            </div>

            <p class="text-center text-muted-sub mt-5 italic">
              Tài liệu này được tạo tự động từ mã nguồn hệ thống KGVH Hưng Yên. Để biết thêm thông tin hoặc yêu cầu endpoint staging, vui lòng liên hệ đội kỹ thuật.
            </p>
          </section>
        </div>
      </main>

      <!-- Sandbox Panel (Right Side, visible when activeTab is sandbox or when sandbox mode is triggered) -->
      <aside v-show="activeTab === 'sandbox'" class="sandbox-panel">
        <div class="sandbox-header">
          <h3><i class="fas fa-terminal"></i> API Sandbox</h3>
          <button class="reset-btn" @click="resetSandbox">
            <i class="fas fa-undo"></i> Khôi phục
          </button>
        </div>

        <div class="sandbox-fields">
          <div class="sandbox-field-group">
            <label class="sandbox-label">HTTP Method & URL</label>
            <div class="url-input-row">
              <select v-model="sandboxMethod" class="sandbox-select">
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
              <input v-model="sandboxUrl" type="text" class="sandbox-input url-input" placeholder="http://..." />
            </div>
          </div>

          <div class="sandbox-field-group">
            <label class="sandbox-label">Authorization Token (Bearer)</label>
            <input v-model="sandboxToken" type="text" class="sandbox-input" placeholder="Nhập token xác thực..." />
          </div>

          <div class="sandbox-field-group">
            <label class="sandbox-label">Headers (JSON)</label>
            <textarea v-model="sandboxHeaders" class="sandbox-textarea code-font" placeholder="{ &quot;Device-Id&quot;: &quot;1&quot; }"></textarea>
          </div>

          <div v-if="sandboxMethod !== 'GET'" class="sandbox-field-group">
            <label class="sandbox-label">Request Body (JSON)</label>
            <textarea v-model="sandboxBody" class="sandbox-textarea body-textarea code-font" placeholder="{}"></textarea>
          </div>

          <button
            class="send-request-btn"
            :disabled="sendingRequest"
            @click="sendSandboxRequest"
          >
            <span v-if="sendingRequest"><i class="fas fa-spinner fa-spin"></i> Đang gửi...</span>
            <span v-else><i class="fas fa-paper-plane"></i> Gửi Yêu Cầu</span>
          </button>
        </div>

        <!-- Sandbox Output -->
        <div class="sandbox-output-section">
          <div class="output-header">
            <h4>Phản Hồi Thực Tế</h4>
            <span v-if="responseStatus" :class="['status-badge', responseStatus >= 400 ? 'status-error' : 'status-ok']">
              {{ responseStatus }}
            </span>
          </div>

          <div class="output-screen">
            <div v-if="sendingRequest" class="loading-screen">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p>Đang kết nối tới server...</p>
            </div>
            <div v-else-if="!responseReceived" class="placeholder-screen">
              Nhấp nút "Gửi Yêu Cầu" để gọi API thực tế.
            </div>
            <div v-else class="response-display">
              <div class="response-meta">
                <span>Thời gian: {{ responseTime }}ms</span>
                <span>Kích thước: {{ responseSize }} bytes</span>
              </div>
              <pre class="code-pre"><code v-html="highlightJson(responseBody)"></code></pre>
            </div>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ApiDocsPublic',
  data() {
    return {
      activeTab: 'docs', // 'docs' or 'sandbox'
      activeDoc: 'overall', // 'overall' or 'details'
      searchQuery: '',
      selectedSection: null,
      logoError: false,

      // Sections lists for sidebar navigation
      overallSections: [
        { id: 'ov-purpose', title: 'Mục đích tài liệu này', icon: 'fas fa-info-circle' },
        { id: 'ov-whatis', title: 'Hệ thống là gì?', icon: 'fas fa-question-circle' },
        { id: 'ov-apps', title: 'Hai ứng dụng độc lập', icon: 'fas fa-cubes' },
        { id: 'ov-urls', title: 'URL Production', icon: 'fas fa-link' },
        { id: 'ov-folders', title: 'Cấu trúc thư mục', icon: 'fas fa-folder-open' },
        { id: 'ov-patterns', title: 'Pattern chính', icon: 'fas fa-cogs' },
        { id: 'ov-auth', title: 'Xác thực', icon: 'fas fa-shield-alt' },
        { id: 'ov-roles', title: 'Roles', icon: 'fas fa-user-tag' },
        { id: 'ov-errors', title: 'Mã lỗi HTTP', icon: 'fas fa-exclamation-triangle' },
        { id: 'ov-domains', title: 'Domain dữ liệu chính', icon: 'fas fa-database' },
        { id: 'ov-languages', title: 'Ngôn ngữ hỗ trợ', icon: 'fas fa-language' },
        { id: 'ov-pagination', title: 'Phân trang', icon: 'fas fa-list-ol' },
        { id: 'ov-background', title: 'Background & Sync', icon: 'fas fa-sync' },
        { id: 'ov-docs', title: 'Tài liệu liên quan', icon: 'fas fa-file-alt' },
        { id: 'ov-deployment', title: 'Deployment', icon: 'fas fa-server' }
      ],

      detailsSections: [
        { id: 'dt-overview', title: '1. Tổng Quan Hệ Thống', icon: 'fas fa-info-circle' },
        { id: 'dt-urls', title: '2. Base URL & Môi Trường', icon: 'fas fa-link' },
        { id: 'dt-auth', title: '3. Cơ Chế Xác Thực', icon: 'fas fa-key' },
        { id: 'dt-roles', title: '4. Phân Quyền Truy Cập', icon: 'fas fa-user-shield' },
        { id: 'dt-errors', title: '5. Mã Lỗi Hệ Thống', icon: 'fas fa-exclamation-triangle' },
        { id: 'dt-sync', title: '6. Quy Tắc Đồng Bộ Dữ Liệu', icon: 'fas fa-sync-alt' },
        { id: 'dt-common', title: '7. Chuẩn Dữ Liệu Chung', icon: 'fas fa-database' },
        { id: 'dt-admin', title: '8. Admin API Endpoints', icon: 'fas fa-user-cog' },
        { id: 'dt-public', title: '9. Public API Endpoints', icon: 'fas fa-users' },
        { id: 'dt-examples', title: '10. Ví Dụ Phản Hồi Mẫu', icon: 'fas fa-code' }
      ],

      // Subsections and endpoints arrays
      adminSections: [
        {
          title: '8.1 Xác Thực & Quản Lý Phiên',
          id: 'dt-admin-8-1',
          endpoints: [
            { method: 'POST', path: '/auth', auth: 'Không', desc: 'Đăng nhập staff, lấy access token' }
          ]
        },
        {
          title: '8.2 Quản Lý Người Dùng',
          id: 'dt-admin-8-2',
          endpoints: [
            { method: 'GET', path: '/user', auth: 'Có', desc: 'Lấy thông tin người dùng theo ID' },
            { method: 'GET', path: '/user/list', auth: 'Có', desc: 'Danh sách người dùng (phân trang)' },
            { method: 'POST', path: '/user', auth: 'Có', desc: 'Tạo người dùng mới' },
            { method: 'PUT', path: '/user', auth: 'Có', desc: 'Cập nhật thông tin người dùng' },
            { method: 'DELETE', path: '/user', auth: 'Có', desc: 'Xóa người dùng' },
            { method: 'POST', path: '/force-logout', auth: 'Có', desc: 'Buộc đăng xuất người dùng' }
          ]
        },
        {
          title: '8.3 Tổ Chức & IAM',
          id: 'dt-admin-8-3',
          endpoints: [
            { method: 'GET', path: '/organization', auth: 'Có', desc: 'Lấy thông tin tổ chức' },
            { method: 'POST', path: '/organization', auth: 'Có', desc: 'Tạo tổ chức' },
            { method: 'PUT', path: '/organization', auth: 'Có', desc: 'Cập nhật tổ chức' },
            { method: 'DELETE', path: '/organization', auth: 'Có', desc: 'Xóa tổ chức' },
            { method: 'GET', path: '/organization/hierarchy', auth: 'Có', desc: 'Lấy cây phân cấp tổ chức' },
            { method: 'POST', path: '/organization/hierarchy', auth: 'Có', desc: 'Tạo cây phân cấp tổ chức' },
            { method: 'GET', path: '/iam/action', auth: 'Có', desc: 'Lấy danh sách actions (quyền)' },
            { method: 'POST', path: '/iam/action', auth: 'Có', desc: 'Tạo action (quyền) mới' },
            { method: 'GET', path: '/iam/managed-resource', auth: 'Có', desc: 'Lấy danh sách tài nguyên' },
            { method: 'POST', path: '/iam/managed-resource', auth: 'Có', desc: 'Tạo tài nguyên mới' },
            { method: 'GET', path: '/iam/org-action', auth: 'Có', desc: 'Lấy danh sách gán quyền theo tổ chức' },
            { method: 'POST', path: '/iam/org-action', auth: 'Có', desc: 'Gán quyền theo tổ chức' },
            { method: 'GET', path: '/iam/org-action/assignee', auth: 'Có', desc: 'Lấy danh sách gán người thực hiện' },
            { method: 'POST', path: '/iam/org-action/assignee', auth: 'Có', desc: 'Gán người thực hiện' }
          ]
        },
        {
          title: '8.4 Địa Điểm (Place)',
          id: 'dt-admin-8-4',
          endpoints: [
            { method: 'GET', path: '/place', auth: 'Có', desc: 'Lấy chi tiết địa điểm' },
            { method: 'GET', path: '/place/list', auth: 'Có', desc: 'Danh sách địa điểm (phân trang, lọc)' },
            { method: 'POST', path: '/place', auth: 'Có', desc: 'Tạo địa điểm mới' },
            { method: 'PUT', path: '/place', auth: 'Có', desc: 'Cập nhật địa điểm' },
            { method: 'DELETE', path: '/place', auth: 'Có', desc: 'Xóa địa điểm' },
            { method: 'GET', path: '/place_categories', auth: 'Có', desc: 'Lấy danh mục địa điểm' },
            { method: 'POST', path: '/place_categories', auth: 'Có', desc: 'Tạo danh mục địa điểm' },
            { method: 'GET', path: '/place/group', auth: 'Có', desc: 'Lấy nhóm địa điểm' },
            { method: 'POST', path: '/place/group', auth: 'Có', desc: 'Tạo nhóm địa điểm' },
            { method: 'GET', path: '/place/group/list', auth: 'Có', desc: 'Danh sách nhóm địa điểm' },
            { method: 'POST', path: '/place/group/list', auth: 'Có', desc: 'Tạo danh sách nhóm địa điểm' },
            { method: 'GET', path: '/place/group/item', auth: 'Có', desc: 'Lấy item trong nhóm' },
            { method: 'POST', path: '/place/group/item', auth: 'Có', desc: 'Tạo item trong nhóm' },
            { method: 'GET', path: '/place/related', auth: 'Có', desc: 'Lấy địa điểm liên quan' },
            { method: 'POST', path: '/place/related', auth: 'Có', desc: 'Tạo liên kết địa điểm' }
          ]
        },
        {
          title: '8.5 Hiện Vật (Danh sách) / Bảo Tàng (Artifact)',
          id: 'dt-admin-8-5',
          endpoints: [
            { method: 'GET', path: '/artifact', auth: 'Có', desc: 'Lấy chi tiết hiện vật' },
            { method: 'GET', path: '/artifact/list', auth: 'Có', desc: 'Danh sách hiện vật' },
            { method: 'POST', path: '/artifact', auth: 'Có', desc: 'Tạo hiện vật' },
            { method: 'PUT', path: '/artifact', auth: 'Có', desc: 'Cập nhật hiện vật' },
            { method: 'DELETE', path: '/artifact', auth: 'Có', desc: 'Xóa hiện vật' },
            { method: 'GET', path: '/artifact/classification', auth: 'Có', desc: 'Lấy phân loại hiện vật' },
            { method: 'POST', path: '/artifact/classification', auth: 'Có', desc: 'Tạo phân loại hiện vật' }
          ]
        },
        {
          title: '8.6 Trang Banner',
          id: 'dt-admin-8-6',
          endpoints: [
            { method: 'GET', path: '/banners', auth: 'Có', desc: 'Lấy danh sách banner quảng cáo' },
            { method: 'POST', path: '/banners', auth: 'Có', desc: 'Quản lý banner quảng cáo' },
            { method: 'GET', path: '/display-model', auth: 'Có', desc: 'Lấy mô hình hiển thị' },
            { method: 'POST', path: '/display-model', auth: 'Có', desc: 'Mô hình hiển thị - Danh sách dữ liệu trên Banner' }
          ]
        },
        {
          title: '8.7 Hệ Thống',
          id: 'dt-admin-8-7',
          endpoints: [
            { method: 'GET', path: '/storage', auth: 'Có', desc: 'Lấy file lưu trữ' },
            { method: 'POST', path: '/storage', auth: 'Có', desc: 'Upload file' },
            { method: 'GET', path: '/storage/list', auth: 'Có', desc: 'Danh sách file' },
            { method: 'GET', path: '/comment', auth: 'Có', desc: 'Xem comment' },
            { method: 'POST', path: '/comment', auth: 'Có', desc: 'Bình luận' },
            { method: 'GET', path: '/comment/list', auth: 'Có', desc: 'Danh sách bình luận' },
            { method: 'GET', path: '/activity-log', auth: 'Có', desc: 'Nhật ký hoạt động' }
          ]
        }
      ],

      publicSections: [
        {
          title: '9.1 Xác Thực',
          id: 'dt-public-9-1',
          endpoints: [
            { method: 'POST', path: '/auth', auth: 'Không', desc: 'Đăng nhập bằng email/mật khẩu' },
            { method: 'POST', path: '/register', auth: 'Không', desc: 'Đăng ký tài khoản mới' },
            { method: 'POST', path: '/facebook-auth', auth: 'Không', desc: 'Đăng nhập qua Facebook' },
            { method: 'POST', path: '/google-auth', auth: 'Không', desc: 'Đăng nhập qua Google' },
            { method: 'POST', path: '/apple-auth', auth: 'Không', desc: 'Đăng nhập qua Apple' },
            { method: 'POST', path: '/logout', auth: 'Có', desc: 'Đăng xuất' },
            { method: 'POST', path: '/create-refresh-token', auth: 'Có', desc: 'Tạo refresh token' },
            { method: 'POST', path: '/refresh-token', auth: 'Không', desc: 'Đổi refresh token → access token' },
            { method: 'POST', path: '/profile/send_token', auth: 'Không', desc: 'Gửi token đặt lại mật khẩu về email' },
            { method: 'POST', path: '/profile/reset_password', auth: 'Không', desc: 'Đặt lại mật khẩu bằng token' }
          ]
        },
        {
          title: '9.2 Hồ Sơ Người Dùng',
          id: 'dt-public-9-2',
          endpoints: [
            { method: 'GET', path: '/profile', auth: 'Có', desc: 'Lấy thông tin hồ sơ' },
            { method: 'PUT', path: '/profile', auth: 'Có', desc: 'Cập nhật hồ sơ' }
          ]
        },
        {
          title: '9.3 Địa Điểm (Place)',
          id: 'dt-public-9-3',
          endpoints: [
            { method: 'GET', path: '/place', auth: 'Không', desc: 'Chi tiết địa điểm theo ID' },
            { method: 'GET', path: '/place/<slug>', auth: 'Không', desc: 'Chi tiết địa điểm theo slug' },
            { method: 'GET', path: '/place/list', auth: 'Không', desc: 'Danh sách địa điểm có lọc' },
            { method: 'GET', path: '/place/artifact', auth: 'Không', desc: 'Hiện vật của địa điểm' },
            { method: 'GET', path: '/place_categories', auth: 'Không', desc: 'Danh mục địa điểm' },
            { method: 'GET', path: '/place/group/list', auth: 'Không', desc: 'Nhóm địa điểm' },
            { method: 'GET', path: '/place/group/item', auth: 'Không', desc: 'Item trong nhóm' },
            { method: 'GET', path: '/place/related', auth: 'Không', desc: 'Địa điểm liên quan' },
            { method: 'GET', path: '/qr-code/get-place', auth: 'Không', desc: 'Lấy địa điểm từ mã QR' }
          ]
        },
        {
          title: '9.4 Bài Viết (Post)',
          id: 'dt-public-9-4',
          endpoints: [
            { method: 'GET', path: '/post', auth: 'Không', desc: 'Chi tiết bài viết theo ID' },
            { method: 'GET', path: '/post/<slug>', auth: 'Không', desc: 'Chi tiết bài viết theo slug' },
            { method: 'GET', path: '/post/list', auth: 'Không', desc: 'Danh sách bài viết' },
            { method: 'GET', path: '/post/category', auth: 'Không', desc: 'Danh mục bài viết' },
            { method: 'GET', path: '/seo/post/<locale>/<slug>', auth: 'Không', desc: 'Dữ liệu SEO bài viết' }
          ]
        },
        {
          title: '9.5 Tour Du Lịch',
          id: 'dt-public-9-5',
          endpoints: [
            { method: 'GET', path: '/tour', auth: 'Không', desc: 'Chi tiết tour theo ID' },
            { method: 'GET', path: '/tour/list', auth: 'Không', desc: 'Danh sách tour' },
            { method: 'GET', path: '/tour/day', auth: 'Không', desc: 'Lịch trình ngày tour' },
            { method: 'GET', path: '/tour/day/list', auth: 'Không', desc: 'Danh sách ngày tour' },
            { method: 'GET', path: '/tour/destination', auth: 'Không', desc: 'Điểm đến trong ngày' },
            { method: 'GET', path: '/tour/destination/list', auth: 'Không', desc: 'Danh sách điểm đến' },
            { method: 'GET', path: '/tour/related', auth: 'Không', desc: 'Tour liên quan' }
          ]
        },
        {
          title: '9.6 Hiện Vật / Bảo Tàng (Artifact)',
          id: 'dt-public-9-6',
          endpoints: [
            { method: 'GET', path: '/artifact', auth: 'Không', desc: 'Chi tiết hiện vật' },
            { method: 'GET', path: '/artifact/list', auth: 'Không', desc: 'Danh sách hiện vật' },
            { method: 'GET', path: '/artifact/classification', auth: 'Không', desc: 'Phân loại hiện vật' }
          ]
        },
        {
          title: '9.7 Tìm Kiếm',
          id: 'dt-public-9-7',
          endpoints: [
            { method: 'GET', path: '/search/text', auth: 'Không', desc: 'Tìm kiếm toàn văn' }
          ]
        },
        {
          title: '9.8 Chiến Dịch & Nội Dung Khuyến Mãi',
          id: 'dt-public-9-8',
          endpoints: [
            { method: 'GET', path: '/campaign', auth: 'Không', desc: 'Chi tiết chiến dịch' },
            { method: 'GET', path: '/campaign/list', auth: 'Không', desc: 'Danh sách chiến dịch' },
            { method: 'GET', path: '/campaign/check', auth: 'Không', desc: 'Kiểm tra điều kiện chiến dịch' },
            { method: 'GET', path: '/campaign/popup', auth: 'Không', desc: 'Popup chiến dịch' },
            { method: 'GET', path: '/campaign/unread', auth: 'Không', desc: 'Chiến dịch chưa đọc' },
            { method: 'GET', path: '/banner', auth: 'Không', desc: 'Danh sách banner' }
          ]
        },
        {
          title: '9.9 Ẩm Thực',
          id: 'dt-public-9-9',
          endpoints: [
            { method: 'GET', path: '/dish', auth: 'Không', desc: 'Chi tiết món ăn' },
            { method: 'GET', path: '/dish/group', auth: 'Không', desc: 'Nhóm món ăn' },
            { method: 'GET', path: '/cuisine-menu', auth: 'Không', desc: 'Menu nhà hàng' },
            { method: 'GET', path: '/menu-item', auth: 'Không', desc: 'Mục menu' },
            { method: 'GET', path: '/menu-item/list', auth: 'Không', desc: 'Danh sách mục menu' }
          ]
        },
        {
          title: '9.10 Gói Dịch Vụ & Phòng',
          id: 'dt-public-9-10',
          endpoints: [
            { method: 'GET', path: '/package', auth: 'Không', desc: 'Gói dịch vụ' },
            { method: 'GET', path: '/room-type', auth: 'Không', desc: 'Loại phòng' },
            { method: 'GET', path: '/room-type/list', auth: 'Không', desc: 'Danh sách loại phòng' }
          ]
        },
        {
          title: '9.11 Tương Tác Người Dùng',
          id: 'dt-public-9-11',
          endpoints: [
            { method: 'GET', path: '/comment', auth: 'Không', desc: 'Đọc bình luận (GET)' },
            { method: 'POST', path: '/comment', auth: 'Có', desc: 'Viết bình luận (POST)' },
            { method: 'GET', path: '/comment/list', auth: 'Không', desc: 'Danh sách bình luận' },
            { method: 'POST', path: '/like', auth: 'Có', desc: 'Thích' },
            { method: 'DELETE', path: '/like', auth: 'Có', desc: 'Bỏ thích' },
            { method: 'POST', path: '/rate', auth: 'Có', desc: 'Tạo đánh giá' },
            { method: 'PUT', path: '/rate', auth: 'Có', desc: 'Cập nhật đánh giá' }
          ]
        },
        {
          title: '9.12 Giỏ Hàng & Thanh Toán',
          id: 'dt-public-9-12',
          endpoints: [
            { method: 'GET', path: '/cart', auth: 'Có', desc: 'Xem giỏ hàng' },
            { method: 'POST', path: '/cart', auth: 'Có', desc: 'Thêm giỏ hàng' },
            { method: 'POST', path: '/cart/purchase', auth: 'Có', desc: 'Thanh toán giỏ hàng' },
            { method: 'POST', path: '/cart/top_up', auth: 'Có', desc: 'Nạp tiền' },
            { method: 'POST', path: '/receipt', auth: 'Có', desc: 'Tạo đơn hàng' },
            { method: 'GET', path: '/code', auth: 'Không', desc: 'Xem mã giảm giá (GET)' },
            { method: 'POST', path: '/code', auth: 'Không', desc: 'Áp dụng mã giảm giá (POST)' }
          ]
        },
        {
          title: '9.13 Board & Yêu Thích',
          id: 'dt-public-9-13',
          endpoints: [
            { method: 'GET', path: '/boards', auth: 'Có', desc: 'Danh sách board cá nhân' },
            { method: 'GET', path: '/board-items', auth: 'Có', desc: 'Xem item yêu thích' },
            { method: 'POST', path: '/board-items', auth: 'Có', desc: 'Thêm item yêu thích' },
            { method: 'DELETE', path: '/board-items', auth: 'Có', desc: 'Xóa item yêu thích' },
            { method: 'GET', path: '/board-items/check', auth: 'Có', desc: 'Kiểm tra đã lưu chưa' }
          ]
        },
        {
          title: '9.14 File & Media',
          id: 'dt-public-9-14',
          endpoints: [
            { method: 'GET', path: '/storage', auth: 'Không', desc: 'Lấy file' },
            { method: 'POST', path: '/storage', auth: 'Có', desc: 'Upload file (POST)' },
            { method: 'PUT', path: '/storage', auth: 'Có', desc: 'Upload file (PUT)' },
            { method: 'POST', path: '/storage/url', auth: 'Có', desc: 'Tạo file từ URL' }
          ]
        },
        {
          title: '9.15 SEO & Bản đồ',
          id: 'dt-public-9-15',
          endpoints: [
            { method: 'GET', path: '/seo/place/<locale>/<slug>', auth: 'Không', desc: 'SEO data địa điểm' },
            { method: 'GET', path: '/seo/artifact/<locale>/<slug>', auth: 'Không', desc: 'SEO data hiện vật' },
            { method: 'GET', path: '/goong/autocomplete', auth: 'Không', desc: 'Gợi ý địa chỉ (Goong Maps)' },
            { method: 'GET', path: '/goong/detail', auth: 'Không', desc: 'Chi tiết địa chỉ (Goong Maps)' }
          ]
        },
        {
          title: '9.16 Dữ Liệu Hỗ Trợ',
          id: 'dt-public-9-16',
          endpoints: [
            { method: 'GET', path: '/display-model', auth: 'Không', desc: 'Mô hình hiển thị' },
            { method: 'GET', path: '/constant/list', auth: 'Không', desc: 'Danh sách hằng số hệ thống' },
            { method: 'GET', path: '/currency', auth: 'Không', desc: 'Thông tin tiền tệ' }
          ]
        }
      ],

      // Section 10 Examples Tab States
      exampleTabs: {
        '10-1': 'req',
        '10-2': 'req',
        '10-3': 'req',
        '10-4': 'req',
        '10-5': 'res401',
        '10-6': 'req'
      },

      // Sandbox variables
      sandboxMethod: 'GET',
      sandboxUrl: '',
      sandboxToken: '',
      sandboxHeaders: '{}',
      sandboxBody: '{}',
      sendingRequest: false,
      responseReceived: false,
      responseStatus: null,
      responseTime: 0,
      responseSize: 0,
      responseBody: ''
    }
  },
  computed: {
    // Search filter logic for document sections
    filteredSections() {
      const query = this.searchQuery.trim().toLowerCase()
      const currentSections = this.activeDoc === 'overall' ? this.overallSections : this.detailsSections
      if (!query) return currentSections

      return currentSections.filter(sec => sec.title.toLowerCase().includes(query))
    },

    // Flat list of all endpoints to support quick search in the sidebar
    allEndpoints() {
      const list = []
      this.adminSections.forEach(s => {
        s.endpoints.forEach(e => {
          list.push({ ...e, apiType: 'admin', groupTitle: s.title, sectionId: 'dt-admin' })
        })
      })
      this.publicSections.forEach(s => {
        s.endpoints.forEach(e => {
          list.push({ ...e, apiType: 'public', groupTitle: s.title, sectionId: 'dt-public' })
        })
      })
      return list
    },

    // Search filter logic for endpoints
    filteredEndpoints() {
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return []

      return this.allEndpoints.filter(ep =>
        ep.path.toLowerCase().includes(query) ||
        ep.desc.toLowerCase().includes(query) ||
        ep.method.toLowerCase().includes(query)
      )
    }
  },
  methods: {
    scrollToSection(id) {
      this.selectedSection = id
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    handleLogoError() {
      this.logoError = true
    },
    tryEndpoint(apiType, path) {
      const prefix = apiType === 'admin'
        ? 'https://admin.hy.khonggianvanhoa.vn/api/admin'
        : 'https://admin.hy.khonggianvanhoa.vn/api/public'

      let match = null
      if (apiType === 'admin') {
        // Search in admin sections
        for (const s of this.adminSections) {
          const found = s.endpoints.find(e => e.path === path)
          if (found) { match = found; break }
        }
      } else {
        // Search in public sections
        for (const s of this.publicSections) {
          const found = s.endpoints.find(e => e.path === path)
          if (found) { match = found; break }
        }
      }

      this.sandboxMethod = match ? match.method : 'GET'

      // Prepopulate placeholder values for path params
      let formattedPath = path
      formattedPath = formattedPath.replace('<slug>', 'ho-hoan-kiem')
      formattedPath = formattedPath.replace('<locale>', 'vi')

      this.sandboxUrl = `${prefix}${formattedPath}`

      // Pre-fill header structure
      const headersObj = {}
      if (match && match.auth === 'Có') {
        headersObj['Authorization'] = 'Bearer <access_token>'
      }
      if (apiType === 'public') {
        headersObj['Device-Id'] = 'device-abc-123'
      }
      this.sandboxHeaders = JSON.stringify(headersObj, null, 2)
      this.sandboxBody = '{}'

      // Open sandbox panel
      this.activeTab = 'sandbox'
    },
    resetSandbox() {
      this.sandboxMethod = 'GET'
      this.sandboxUrl = ''
      this.sandboxToken = ''
      this.sandboxHeaders = '{}'
      this.sandboxBody = '{}'
      this.responseReceived = false
      this.responseStatus = null
    },
    sendSandboxRequest() {
      this.sendingRequest = true
      this.responseReceived = false

      let headers = {}
      try {
        headers = JSON.parse(this.sandboxHeaders)
      } catch (e) {
        this.$message.error('Headers JSON không hợp lệ')
        this.sendingRequest = false
        return
      }

      if (this.sandboxToken) {
        headers['Authorization'] = `Bearer ${this.sandboxToken}`
      }

      let data = null
      if (this.sandboxMethod !== 'GET') {
        try {
          data = JSON.parse(this.sandboxBody)
        } catch (e) {
          this.$message.error('Request Body JSON không hợp lệ')
          this.sendingRequest = false
          return
        }
      }

      const startTime = Date.now()

      axios({
        method: this.sandboxMethod,
        url: this.sandboxUrl,
        headers,
        data
      })
        .then(response => {
          this.responseTime = Date.now() - startTime
          this.responseStatus = response.status
          this.responseBody = JSON.stringify(response.data, null, 2)
          this.responseSize = this.responseBody.length
          this.responseReceived = true
        })
        .catch(err => {
          this.responseTime = Date.now() - startTime
          this.responseStatus = err.response ? err.response.status : 'ERROR'
          const errData = err.response ? err.response.data : { error: err.message }
          this.responseBody = JSON.stringify(errData, null, 2)
          this.responseSize = this.responseBody.length
          this.responseReceived = true
        })
        .finally(() => {
          this.sendingRequest = false
        })
    },
    highlightJson(json) {
      if (!json) return ''
      if (typeof json !== 'string') {
        json = JSON.stringify(json, undefined, 2)
      }
      json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

      return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, function (match) {
        let cls = 'number'
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'key'
          } else {
            cls = 'string'
          }
        } else if (/true|false/.test(match)) {
          cls = 'boolean'
        } else if (/null/.test(match)) {
          cls = 'null'
        }
        return '<span class="hl-' + cls + '">' + match + '</span>'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;500&family=Inter:wght@300;400;500;600;700;800&display=swap');

// Design Palette
$bg-main: #0a0d16;
$bg-card: #121824;
$bg-input: #1b2336;
$border-color: #212d45;
$text-main: #f3f4f6;
$text-muted: #9ca3af;
$text-muted-sub: #7b8599;
$primary: #10b981;
$secondary: #3b82f6;
$warning: #f59e0b;
$danger: #ef4444;

.api-docs-portal {
  font-family: 'Inter', sans-serif;
  background-color: $bg-main;
  color: $text-main;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  // Header styles
  .api-header {
    background-color: rgba($bg-card, 0.9);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid $border-color;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);

    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .back-home-btn {
      color: $text-muted;
      font-size: 16px;
      padding: 8px 12px;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.06);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease-in-out;
      text-decoration: none;

      &:hover {
        color: $text-main;
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.15);
        transform: translateX(-2px);
      }
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 12px;

      .brand-logo {
        height: 38px;
        object-fit: contain;
      }

      .brand-text {
        display: flex;
        flex-direction: column;
      }

      .brand-title {
        font-size: 18px;
        font-weight: 800;
        margin: 0;
        letter-spacing: -0.5px;
        background: linear-gradient(135deg, #10b981 0%, #3b82f6 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .brand-subtitle {
        font-size: 11px;
        color: $text-muted;
        margin: 2px 0 0 0;
      }
    }

    .tab-controllers {
      display: flex;
      background: rgba(0, 0, 0, 0.3);
      padding: 4px;
      border-radius: 8px;
      border: 1px solid $border-color;
      gap: 4px;

      .tab-btn {
        background: transparent;
        border: none;
        color: $text-muted;
        font-size: 13px;
        font-weight: 600;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s;

        &:hover {
          color: $text-main;
          background: rgba(255, 255, 255, 0.03);
        }

        &.active {
          color: #ffffff;
          background: $secondary;
          box-shadow: 0 2px 8px rgba($secondary, 0.3);
        }
      }
    }
  }

  // Layout structures
  .portal-body {
    flex: 1;
    display: flex;
    overflow: hidden;
    height: calc(100vh - 65px);
  }

  // Sidebar styles
  .docs-sidebar {
    width: 320px;
    border-right: 1px solid $border-color;
    background-color: rgba($bg-card, 0.5);
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    flex-shrink: 0;

    .doc-selector {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
      background: rgba(0, 0, 0, 0.3);
      padding: 4px;
      border-radius: 8px;
      border: 1px solid $border-color;

      .doc-selector-btn {
        background: transparent;
        border: none;
        color: $text-muted;
        font-size: 12px;
        font-weight: 600;
        padding: 8px 4px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s;
        text-align: center;

        &:hover {
          color: $text-main;
        }

        &.active {
          color: white;
          background: $bg-input;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
      }
    }

    .search-box {
      position: relative;

      .search-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: $text-muted-sub;
        font-size: 14px;
      }

      .search-input {
        width: 100%;
        background: $bg-input;
        border: 1px solid $border-color;
        color: $text-main;
        padding: 10px 12px 10px 36px;
        border-radius: 8px;
        font-size: 13px;
        transition: all 0.2s;

        &:focus {
          outline: none;
          border-color: $secondary;
          box-shadow: 0 0 0 2px rgba($secondary, 0.15);
        }
      }
    }

    .sidebar-nav {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .nav-group-title {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        color: $text-muted-sub;
        letter-spacing: 0.5px;
        margin-top: 12px;
        margin-bottom: 6px;
        padding-left: 8px;
      }

      .nav-item {
        padding: 8px 12px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 500;
        color: $text-muted;
        display: flex;
        align-items: center;
        gap: 10px;
        transition: all 0.2s;

        &:hover {
          color: $text-main;
          background: rgba(255, 255, 255, 0.03);
        }

        &.active {
          color: #ffffff;
          background: rgba($secondary, 0.1);
          border-left: 3px solid $secondary;
          padding-left: 9px;
        }

        i {
          width: 16px;
          text-align: center;
          font-size: 14px;
          color: $text-muted-sub;
        }

        &:hover i {
          color: $secondary;
        }
      }

      .endpoint-nav-item {
        padding: 6px 10px;
        display: flex;
        align-items: center;
        gap: 8px;

        .endpoint-nav-path {
          font-family: 'Fira Code', monospace;
          font-size: 11px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex: 1;
        }
      }

      .empty-nav {
        padding: 16px;
        text-align: center;
        font-size: 12px;
        color: $text-muted-sub;
        font-style: italic;
      }
    }
  }

  // Main content panel
  .docs-content {
    flex: 1;
    padding: 32px 48px;
    overflow-y: auto;
    scroll-behavior: smooth;
  }

  .document-view {
    max-width: 900px;
    margin: 0 auto;
  }

  .doc-header {
    margin-bottom: 24px;

    .main-doc-title {
      font-size: 32px;
      font-weight: 800;
      letter-spacing: -1px;
      line-height: 1.25;
      margin: 0 0 12px 0;
      background: linear-gradient(135deg, #ffffff 0%, $text-muted 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .doc-meta {
      display: flex;
      gap: 16px;

      .meta-tag {
        font-size: 12px;
        color: $text-muted-sub;
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.05);
        padding: 4px 10px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        gap: 6px;
      }
    }
  }

  .doc-divider {
    border: 0;
    height: 1px;
    background: linear-gradient(90deg, $border-color 0%, rgba($border-color, 0) 100%);
    margin: 24px 0;
  }

  // Sections
  .doc-section {
    margin-bottom: 40px;
    scroll-margin-top: 80px;

    .section-title {
      font-size: 20px;
      font-weight: 700;
      color: #ffffff;
      margin-top: 0;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 10px;

      i {
        color: $secondary;
      }
    }

    h4 {
      font-size: 16px;
      font-weight: 600;
      margin-top: 24px;
      margin-bottom: 12px;
      color: #ffffff;
    }

    h5 {
      font-size: 13px;
      font-weight: 600;
      color: $text-muted;
      margin-top: 16px;
      margin-bottom: 8px;
    }
  }

  // Sub-section card
  .sub-section-card {
    background: rgba($bg-card, 0.5);
    border: 1px solid $border-color;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    scroll-margin-top: 80px;

    .sub-section-title {
      font-size: 16px;
      font-weight: 700;
      margin-top: 0;
      margin-bottom: 16px;
      color: #ffffff;
      border-bottom: 1px solid rgba($border-color, 0.5);
      padding-bottom: 8px;
    }
  }

  // Lists
  .styled-list {
    margin: 0;
    padding-left: 20px;

    li {
      margin-bottom: 8px;
      line-height: 1.5;
      font-size: 14px;
      color: $text-muted;

      strong {
        color: #ffffff;
      }
    }
  }

  // Custom cards
  .card {
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
    font-size: 14px;
    line-height: 1.5;

    &.card-accent-blue {
      background: rgba($secondary, 0.05);
      border: 1px solid rgba($secondary, 0.2);
      color: #ffffff;

      strong {
        color: $secondary;
      }
    }
  }

  // Lang box
  .lang-box {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;

    .lang-tag {
      background: rgba(255, 255, 255, 0.03);
      border: 1px solid $border-color;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 13px;
      color: #ffffff;
      font-weight: 600;
    }
  }

  // tables
  .table-container {
    background: rgba($bg-card, 0.8);
    border: 1px solid $border-color;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 16px;
  }

  .api-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th, td {
      padding: 12px 16px;
      text-align: left;
      border-bottom: 1px solid $border-color;
      vertical-align: middle;
    }

    th {
      background: rgba(0, 0, 0, 0.2);
      font-weight: 600;
      color: $text-muted-sub;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    td {
      color: $text-muted;

      strong {
        color: #ffffff;
      }

      code {
        font-family: 'Fira Code', monospace;
        color: #e74c3c;
        background: rgba(231, 76, 60, 0.08);
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 12px;
      }

      .text-secondary {
        color: $secondary !important;
      }
    }

    tr:last-child td {
      border-bottom: none;
    }

    .action-cell {
      text-align: right;
    }
  }

  // Method badges
  .method-badge {
    font-size: 9px;
    font-weight: 800;
    padding: 3px 6px;
    border-radius: 4px;
    text-transform: uppercase;
    width: 52px;
    display: inline-block;
    text-align: center;
    flex-shrink: 0;

    &.get { background: rgba($secondary, 0.12); color: $secondary; border: 1px solid rgba($secondary, 0.2); }
    &.post { background: rgba($primary, 0.12); color: $primary; border: 1px solid rgba($primary, 0.2); }
    &.put { background: rgba($warning, 0.12); color: $warning; border: 1px solid rgba($warning, 0.2); }
    &.patch { background: rgba(#9b59b6, 0.12); color: #9b59b6; border: 1px solid rgba(#9b59b6, 0.2); }
    &.delete { background: rgba($danger, 0.12); color: $danger; border: 1px solid rgba($danger, 0.2); }
  }

  // Try buttons
  .try-btn-sm {
    background: rgba($primary, 0.1);
    border: 1px solid rgba($primary, 0.3);
    color: $primary;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: all 0.2s;

    &:hover {
      background: $primary;
      color: white;
      box-shadow: 0 0 8px rgba($primary, 0.4);
    }
  }

  // ASCII flows
  .ascii-flow {
    font-family: 'Fira Code', monospace;
    font-size: 12px;
    line-height: 1.4;
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid $border-color;
    padding: 16px;
    border-radius: 8px;
    color: $text-muted;
    overflow-x: auto;
    margin: 12px 0;
  }

  // Code snippets
  .code-block-wrapper {
    background: #0b0f19;
    border: 1px solid $border-color;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 16px;

    .code-block-header {
      background: rgba(255, 255, 255, 0.02);
      padding: 8px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid $border-color;

      .code-lang {
        font-size: 10px;
        text-transform: uppercase;
        font-weight: 700;
        color: $text-muted-sub;
      }
    }

    .code-pre {
      margin: 0;
      padding: 16px;
      overflow-x: auto;
      font-family: 'Fira Code', monospace;
      font-size: 13px;
      line-height: 1.5;
      color: #c9d1d9;
    }
  }

  // Tabbed example code view in 10
  .example-card {
    background: rgba($bg-card, 0.4);
    border: 1px solid $border-color;
    border-radius: 12px;
    padding: 20px;

    .example-title {
      font-size: 15px;
      font-weight: 700;
      margin: 0 0 14px 0;
      color: #ffffff;
    }

    .tabs-example {
      display: flex;
      gap: 6px;
      margin-bottom: 12px;
      flex-wrap: wrap;

      .tab-ex-btn {
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid $border-color;
        color: $text-muted;
        font-size: 11px;
        font-weight: 600;
        padding: 6px 12px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          color: white;
          background: rgba(255, 255, 255, 0.06);
        }

        &.active {
          background: $secondary;
          color: white;
          border-color: $secondary;
          box-shadow: 0 2px 6px rgba($secondary, 0.3);
        }
      }
    }
  }

  // Sandbox Panel (Right Side)
  .sandbox-panel {
    width: 380px;
    border-left: 1px solid $border-color;
    background-color: rgba($bg-card, 0.6);
    display: flex;
    flex-direction: column;
    flex-shrink: 0;

    .sandbox-header {
      padding: 16px;
      border-bottom: 1px solid $border-color;
      display: flex;
      justify-content: space-between;
      align-items: center;

      h3 {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .reset-btn {
        background: transparent;
        border: none;
        color: $text-muted;
        font-size: 11px;
        cursor: pointer;

        &:hover {
          color: $text-main;
        }
      }
    }

    .sandbox-fields {
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
      border-bottom: 1px solid $border-color;
      overflow-y: auto;
      max-height: 50%;

      .sandbox-field-group {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .sandbox-label {
          font-size: 11px;
          font-weight: 600;
          color: $text-muted;
        }

        .url-input-row {
          display: flex;
          gap: 6px;

          .sandbox-select {
            background: $bg-input;
            border: 1px solid $border-color;
            color: $text-main;
            padding: 8px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 700;
          }

          .url-input {
            flex: 1;
          }
        }

        .sandbox-input, .sandbox-textarea {
          background: $bg-input;
          border: 1px solid $border-color;
          color: $text-main;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 12px;
          width: 100%;

          &:focus {
            outline: none;
            border-color: $secondary;
          }
        }

        .sandbox-textarea {
          font-family: 'Fira Code', monospace;
          resize: vertical;
          height: 60px;

          &.body-textarea {
            height: 90px;
          }
        }
      }

      .send-request-btn {
        background: $primary;
        color: white;
        border: none;
        padding: 10px;
        border-radius: 6px;
        font-weight: 700;
        font-size: 13px;
        cursor: pointer;
        transition: opacity 0.2s;

        &:hover {
          opacity: 0.9;
        }

        &:disabled {
          background: $border-color;
          cursor: not-allowed;
        }
      }
    }

    .sandbox-output-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .output-header {
        padding: 12px 16px;
        background: rgba(0, 0, 0, 0.1);
        border-bottom: 1px solid $border-color;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h4 {
          margin: 0;
          font-size: 12px;
          font-weight: 700;
          color: $text-muted;
        }
      }

      .output-screen {
        flex: 1;
        background: #080b12;
        overflow-y: auto;
        padding: 16px;

        .placeholder-screen {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: $text-muted;
          font-size: 12px;
          font-style: italic;
          text-align: center;
        }

        .loading-screen {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: $text-muted;
          gap: 12px;
          font-size: 12px;
        }

        .response-display {
          display: flex;
          flex-direction: column;
          gap: 12px;

          .response-meta {
            display: flex;
            gap: 12px;
            font-size: 10px;
            color: $text-muted-sub;
          }
        }
      }
    }
  }

  // Utilities
  .mt-2 { margin-top: 8px !important; }
  .mt-3 { margin-top: 12px !important; }
  .mt-4 { margin-top: 16px !important; }
  .mt-5 { margin-top: 24px !important; }
  .mb-2 { margin-bottom: 8px !important; }
  .mb-3 { margin-bottom: 12px !important; }
  .mb-4 { margin-bottom: 16px !important; }
  .py-3 { padding-top: 12px !important; padding-bottom: 12px !important; }
  .text-center { text-align: center !important; }
  .text-warning { color: $warning !important; }
  .italic { font-style: italic !important; }
}
</style>

<!-- JSON highlighting classes inside global scope -->
<style lang="scss">
.hl-key { color: #3b82f6; font-weight: 500; }
.hl-string { color: #10b981; }
.hl-number { color: #f59e0b; }
.hl-boolean { color: #9b59b6; }
.hl-null { color: #7f8c8d; }
</style>
